/* eslint no-mixed-operators: 0 */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { underscored, titleize } from 'underscore.string';
import moment from 'moment';
import DatePicker from 'react-datepicker';
import CargomaticSelect from 'components/v2/select';
import CargomaticInput from 'components/v2/input';
import ErrorAlert from 'components/ui/ErrorAlert';
import Spinner from 'components/ui/Spinner';
import DateIconButton from 'components/v2/buttons/DateIconButton';
import {
  createAccessorialRequest,
  getLaneAccessorialRatesV2,
} from 'utils/AccessorialRequestWebAPIUtils';
import { getAccessorials } from 'utils/AccessorialWebAPIUtils';
import { sluggify } from 'utils/localization/utils';
import ToastStore from 'stores/ToastStore';
import { LOCATION_TYPES } from 'constants/location';
import { getDeliverStop } from 'helpers/stops';
import { withTranslation } from 'react-i18next';
import formatMoney from 'utils/formatters/Money';
import CargomaticAlert from 'components/ui/Alert';
import { CONFIGURED_FEATURES, featureEnabled } from 'helpers/feature-flags';
import SessionStore from 'stores/SessionStore';
import ViewAccessorialRequestDocumentRequirements from 'components/modules/shipments/accessorials/ViewAccessorialDocumentRequirements';
import { STATUS } from 'constants/LaneRates';
import { validDecimal } from 'utils/accounting-util';
import { FEATURE_FLAG_ENABLED_BREADCRUMB } from 'constants/temp-feature-flag-breadcrumb';
import { isEmpty } from 'lodash';

const DATE_FORMAT = 'L LT';

const locationTypes = LOCATION_TYPES.map((l) => ({
  name: titleize(l),
  localizationSlug: l,
  value: l,
}));

function getMomentDate(date) {
  if (!date) {
    return null;
  }

  return moment(date);
}

function isDuplicateAccessorialRequest(accessorialRequest) {
  return (r) => {
    const main =
      r.accessorial_type === accessorialRequest.slug &&
      r.carrier_unit_qty * r.carrier_rate ===
        accessorialRequest.units * accessorialRequest.rate;
    const additional =
      'begin_date' in accessorialRequest &&
      'end_date' in accessorialRequest &&
      'begin_datetime' in r &&
      'end_datetime' in r &&
      accessorialRequest.begin_date &&
      accessorialRequest.end_date &&
      r.begin_datetime &&
      r.end_datetime
        ? moment(accessorialRequest.begin_date).isSame(
            moment(r.begin_datetime),
          ) &&
          moment(accessorialRequest.end_date).isSame(moment(r.end_datetime))
        : main;

    return main && additional;
  };
}

function getNewAccessorialRequest(accessorialRequest) {
  return {
    name: null,
    slug: null,
    units: 0,
    rate: 0,
    admin_note: '',
    description: '',
    begin_date: null,
    end_date: null,
    location: '',
    rate_source: 'manual',
    ...accessorialRequest,
  };
}

function otherSelected(slug) {
  return slug === 'other' || slug === 'Other';
}

const mapAccessorialRequest = (
  job,
  request,
  accessorials,
  customerApprovalProvided,
  customerApprovalRequired,
) => {
  const {
    units: carrier_unit_qty,
    rate: carrier_rate,
    shipper_rate,
    name: message,
    admin_note,
    slug,
    description,
    begin_date: begin_datetime,
    end_date: end_datetime,
    rate_source,
    payable_rate_source,
    receivable_rate_source,
  } = request;

  const accessorial = accessorials.find((acc) => acc.slug === slug);
  const {
    rate_type: unit_name,
    slug: accessorial_type,
    charge_code,
  } = accessorial;
  const { location } = request;

  const {
    _id: shipment,
    shipment_id,
    carrier: { _id: carrier, name: carrier_name },
    shipper: { _id: shipper, company_name: shipper_name },
  } = job;

  let equipment = [];
  const deliverStop = getDeliverStop(job);
  if (deliverStop && deliverStop.equipments && deliverStop.equipments.length) {
    const container = deliverStop.equipments.find((eq) => {
      return eq && eq._equipmentType === 'container';
    });
    if (container) {
      equipment.push(container._id);
    }
  }

  const result = {
    carrier_unit_qty,
    unit_name,
    charge_code,
    carrier_rate,
    shipper_rate,
    admin_note,
    carrier_note: admin_note,
    message,
    accessorial_type,
    type: 'accessorial',
    exception_type: 'Accessorial Request',
    shipment,
    shipment_id,
    carrier_name,
    shipper_name,
    carrier,
    shipper,
    description,
    begin_datetime,
    end_datetime,
    location,
    payee_type: 'carrier',
    equipment,
    master_shipper: job.master_account ? job.master_account.id : null,
    rate_source,
    payable_rate_source,
    receivable_rate_source,
  };
  if (featureEnabled(CONFIGURED_FEATURES.SSR)) {
    if (customerApprovalRequired) {
      if (customerApprovalProvided) {
        result.customer_approval_provided = true;
      } else {
        result.customer_approval_provided = false;
      }
    }
  }
  return result;
};

class AccessorialRequestForm extends Component {
  constructor(props) {
    super(props);
    this.state = {
      accessorial: {},
      accessorialRequest: getNewAccessorialRequest(props.accessorialRequest),
      accessorials: [],
      laneAccessorialRates: {},
      errors: {},
      showNameField: false,
      saving: false,
      success: false,
      step: 1,
      hasCarrierRate: false,
      selectedAccessorial: '',
      customerApprovalRequired: false,
      customerApprovalProvided: false,
      isCarrierMissingRequiredAccDocuments: FEATURE_FLAG_ENABLED_BREADCRUMB
        ? true
        : false,
      hasGenericSupportingDocOnly: false,
    };
  }

  fetchAccessorials = () => {
    this.setState({ loading: true }, async () => {
      try {
        const result = await getAccessorials({
          paginate: false,
          admin_only: false,
        });

        const {
          entity: { items },
        } = result;

        const accessorials = items.map((a) => ({
          ...a,
          slug: a.slug || underscored(a.name.toLowerCase()),
        }));

        this.setState({
          accessorials,
          loading: false,
        });
      } catch {
        ToastStore.addNotification({
          title: 'There was a problem loading the list of accessorials',
          body: '',
          level: 'error',
        });
        this.setState({ loading: false });
      }
    });
  };

  fetchLaneAccessorialRates = async () => {
    const {
      t,
      job: { carrier, market, shipper },
    } = this.props;

    this.setState({ loading: true });
    const laneAccessorialRates = await this.getLaneAccessorialRates(
      {
        customer: [carrier._id, shipper._id],
        market: market.name,
        status: STATUS.CONFIRMED,
      },
      t,
    );
    this.setState({ loading: false });

    const mappedLaneAccessorialRates =
      this.mapLaneAccessorialRates(laneAccessorialRates);

    this.setState({ laneAccessorialRates: mappedLaneAccessorialRates });
  };

  async getLaneAccessorialRates(query, t) {
    const queryString = new URLSearchParams(query).toString();
    try {
      const results = await getLaneAccessorialRatesV2(queryString);
      const {
        data: { laneAccessorialRates },
      } = results;

      if (!laneAccessorialRates || !laneAccessorialRates.length) {
        return [];
      }
      return laneAccessorialRates;
    } catch (e) {
      ToastStore.addNotification({
        title: t('PROBLEM_FETCHING_COSTS'),
        message: e,
        level: 'error',
      });
      return [];
    }
  }

  componentDidMount() {
    this.fetchAccessorials();
    this.fetchLaneAccessorialRates();
  }

  isCarrierRateDisabled() {
    const { hasCarrierRate } = this.state;
    return hasCarrierRate;
  }

  handleUnitsChange = (value) => {
    if (validDecimal(value))
      this.setState((prevState) => {
        const { accessorialRequest } = prevState;

        const units = Number.isNaN(parseFloat(value))
          ? null
          : parseFloat(value);

        return {
          accessorialRequest: {
            ...accessorialRequest,
            units,
          },
        };
      });
  };

  handleRateChange = (value) => {
    this.setState((prevState) => {
      const { accessorialRequest } = prevState;

      const rate = Number.isNaN(parseFloat(value)) ? null : parseFloat(value);

      return {
        accessorialRequest: {
          ...accessorialRequest,
          rate,
          rate_source: 'manual',
          payable_rate_source: 'manual',
        },
      };
    });
  };

  handleLocationChange = (item) => {
    const { value } = item;
    this.setState((prevState) => {
      const { accessorialRequest } = prevState;

      return {
        accessorialRequest: {
          ...accessorialRequest,
          location: value,
        },
      };
    });
  };

  handleValueChange = (field) => (value) => {
    this.setState((prevState) => {
      const { accessorialRequest } = prevState;

      return {
        accessorialRequest: {
          ...accessorialRequest,
          [field]: value,
        },
      };
    });
  };

  calcLaneRate(laneAccessorialRate) {
    const { rate, ratetype, customerType } = laneAccessorialRate;

    if (ratetype !== '%') return rate;

    const { job } = this.props;
    const lineItems =
      customerType === 'carrier' ? job.payables : job.receivables;
    const lineHaul = lineItems.reduce((acc, item) => {
      return item.category === 'base_cost' ? item.value + acc : acc;
    }, 0);

    return parseFloat((rate / 100) * (lineHaul || 0));
  }

  mapLaneAccessorialRates(accessorialRates) {
    const results = {};
    if (Array.isArray(accessorialRates)) {
      accessorialRates.map((accessorialRate) => {
        const accessorialTypeSlug = sluggify(
          accessorialRate.accessorialtype,
        ).toLowerCase();

        if (!results[accessorialTypeSlug]) {
          results[accessorialTypeSlug] = {};
        }

        if (accessorialRate.customerType === 'shipper') {
          results[accessorialTypeSlug].shipper_rate =
            this.calcLaneRate(accessorialRate);
        } else if (accessorialRate.customerType === 'carrier') {
          results[accessorialTypeSlug].carrier_rate =
            this.calcLaneRate(accessorialRate);
        }
      });
    }
    return results;
  }

  setHasGenericSupportingDocOnly = (boolean = false) => {
    if (FEATURE_FLAG_ENABLED_BREADCRUMB) {
      this.setState({
        hasGenericSupportingDocOnly: boolean,
      });
    }
  };

  handleSelectAccessorial = (item) => {
    const { name, slug } = item || {};
    /* Needed to display selected slug since second setState call takes more time than selected expects */
    if (slug) {
      this.setState({
        selectedAccessorial: slug,
        isCarrierMissingRequiredAccDocuments: FEATURE_FLAG_ENABLED_BREADCRUMB
          ? true
          : false,
      });
    }
    this.setState((prevState) => {
      if (!slug) {
        return {
          accessorial: null,
          step: 1,
        };
      }
      const { accessorialRequest, laneAccessorialRates } = prevState;

      const laneAccessorialRate = laneAccessorialRates[slug];

      const rate = laneAccessorialRate?.carrier_rate || 0;
      const shipper_rate = laneAccessorialRate?.shipper_rate || 0;

      const rate_source = rate || shipper_rate ? 'automated' : 'manual';
      const payable_rate_source = rate ? 'automated' : 'manual';
      const receivable_rate_source = shipper_rate ? 'automated' : 'manual';

      const showNameField = otherSelected(slug);

      let { units, begin_date, end_date, description, location } =
        accessorialRequest;

      if (item && item.rate_type === 'flat') {
        units = 1;
      }

      let step = 0.1; // Allowing decimals by default

      if (!item.begin_date_required) {
        begin_date = null;
      }

      if (!item.end_date_required) {
        end_date = null;
      }

      if (!item.description_required) {
        description = null;
      }

      if (!item.location_required) {
        location = null;
      }

      return {
        accessorialRequest: {
          ...accessorialRequest,
          slug,
          name,
          rate,
          shipper_rate,
          units,
          begin_date,
          end_date,
          description,
          location,
          rate_source,
          payable_rate_source,
          receivable_rate_source,
        },
        showNameField,
        laneAccessorialRate,
        accessorial: item,
        step,
        hasCarrierRate: Boolean(rate),
      };
    });
  };

  handleHideErrorAlert = () => {
    this.setState({ errors: {} });
  };

  handleSave = () => {
    this.setState(
      (prevState) => {
        const { accessorialRequest, accessorial } = prevState;
        const errors = this.validate(accessorialRequest, accessorial);
        const saving = Object.keys(errors).length === 0;

        return {
          errors,
          saving,
        };
      },
      async () => {
        if (this.state.saving) {
          try {
            const data = mapAccessorialRequest(
              this.props.job,
              this.state.accessorialRequest,
              this.state.accessorials,
              this.state.customerApprovalProvided,
              this.state.customerApprovalRequired,
            );

            const carrierPartner = SessionStore.getCarrierPartner();
            if (carrierPartner) {
              data.carrierPartner = carrierPartner;
            }

            const result = await createAccessorialRequest(data);
            const {
              entity: { request, success },
            } = result;

            if (!success) {
              this.setState({
                errors: {
                  base: 'There was a problem saving the accessorial request.',
                },
                success: false,
                saving: false,
              });
            } else {
              if (request.autodeclined && request.carrier_decline_reason_code) {
                this.setState({
                  errors: {
                    base: request.carrier_decline_reason_code,
                  },
                  success: false,
                  saving: false,
                });

                return;
              }

              this.setState(
                {
                  saving: false,
                  success: true,
                  errors: {},
                },
                () => {
                  setTimeout(() => {
                    this.props.onSaveSuccess(request);
                  }, 500);
                },
              );
            }
          } catch (e) {
            console.error(e);
            const error = e.entity.error;
            this.setState({
              errors: {
                base:
                  'There was a problem saving the accessorial request.' +
                  (error ? ` (${error})` : ''),
              },
              success: false,
              saving: false,
            });
          }
        }
      },
    );
  };

  handleCancel = () => {
    this.props.onCancel();
  };

  setIsCarrierMissingRequiredAccDocuments = (boolean) => {
    this.setState({
      isCarrierMissingRequiredAccDocuments: boolean,
    });
  };

  actionsDisabled() {
    const { saving, loading, isCarrierMissingRequiredAccDocuments } =
      this.state;

    // Don't allow save if accessorial is disabled
    if (isEmpty(this.state.accessorial)) {
      return true;
    }

    if (loading || saving) {
      return true;
    }

    const allowDocumentRequirementByPass =
      !!this.state.accessorial?.allow_carrier_document_requirement_bypass;

    return (
      isCarrierMissingRequiredAccDocuments && !allowDocumentRequirementByPass
    );
  }

  validate(accessorialRequest, accessorial) {
    const { t, existingRequests } = this.props;
    const {
      name,
      units,
      rate,
      slug,
      begin_date,
      end_date,
      description,
      location,
    } = accessorialRequest;

    const errors = {};
    if (!units || units <= 0) {
      errors.units = t('ONE_UNIT_REQUIRED');
    }

    if (!rate || rate <= 0) {
      errors.rate = t('PROVIDE_RATE');
    }

    if (!slug) {
      errors.slug = t('SELECT_AN_ACCESSORIAL');
    }

    if (otherSelected(slug) && (otherSelected(name) || !name)) {
      errors.name = t('ACCESSORIAL_NAME_REQUIRED');
    }

    if (accessorial && accessorial.begin_date_required && !begin_date) {
      errors.begin_date = t('REQUIRED_BEGIN_DATE');
    }

    if (accessorial && accessorial.end_date_required && !end_date) {
      errors.end_date = t('REQUIRED_END_DATE');
    }

    if (accessorial && accessorial.description_required && !description) {
      errors.description = t('REQUIRED_DESCRIPTION');
    }

    if (accessorial && accessorial.location_required && !location) {
      errors.location = t('REQUIRED_LOCATION');
    }

    if (
      begin_date &&
      end_date &&
      moment(begin_date).isAfter(moment(end_date))
    ) {
      errors.begin_date = t('BEGIN_DATE_AFTER_END_DATE');
    }

    if (
      SessionStore.isCarrier() &&
      existingRequests.some(isDuplicateAccessorialRequest(accessorialRequest))
    ) {
      errors.duplicate_accessorial = t('ACCESSORIAL_ALREADY_REQUESTED');
    }

    return errors;
  }

  renderAccessorialAutoDeclineWarning() {
    const { t } = this.props;
    if (
      featureEnabled(CONFIGURED_FEATURES.SSR) &&
      SessionStore.isCarrier() &&
      !FEATURE_FLAG_ENABLED_BREADCRUMB
    ) {
      return (
        <CargomaticAlert className="my-2" type="warning" title="">
          {t('DOCUMENTATION_UPLOAD_TIMEFRAME')}
        </CargomaticAlert>
      );
    }
    return null;
  }

  renderSuccess() {
    const { t } = this.props;
    const { success, saving } = this.state;

    if (saving || !success) {
      return null;
    }

    return (
      <CargomaticAlert
        type="success"
        onClose={this.handleCloseSuccessAlert}
        canClose
        title={t('ACCESSORIAL_REQUEST_SAVED')}
      />
    );
  }

  renderErrors() {
    const { t } = this.props;
    const { errors } = this.state;

    const errorKeys = Object.keys(errors);

    if (errorKeys.length === 0) {
      return null;
    }

    return (
      <ErrorAlert
        title={t('FIX_FOLLOWING_ISSUES')}
        canClose
        onClose={this.handleHideErrorAlert}
      >
        <ul className="ml-0 list-reset">
          {errorKeys.map((errorKey) => (
            <li key={errorKey}>{this.state.errors[errorKey]}</li>
          ))}
        </ul>
      </ErrorAlert>
    );
  }

  renderTotal() {
    const {
      accessorialRequest: { units, rate },
    } = this.state;

    let totalText;
    if (rate && units) {
      totalText = formatMoney(units * rate, this.props.job?.currency);
    } else {
      totalText = '-';
    }

    return <div className="py-2 font-bold">{totalText}</div>;
  }

  renderRequiredFields() {
    const { t } = this.props;
    const {
      accessorial,
      accessorialRequest: { begin_date, end_date, location },
      errors,
    } = this.state;

    if (!accessorial) {
      return null;
    }

    if (
      !accessorial.begin_date_required &&
      !accessorial.end_date_required &&
      !accessorial.location_required
    ) {
      return null;
    }

    return (
      <div className="flex flex-wrap mb-4 -mx-2">
        {accessorial.begin_date_required && (
          <div className="px-2">
            <label htmlFor="startDate" className="block mb-2 form-label">
              {t('BEGIN_DATE')}
            </label>
            <DatePicker
              id="beginDate"
              selected={getMomentDate(begin_date)}
              onChange={this.handleValueChange('begin_date')}
              customInput={<DateIconButton />}
              dateFormat={DATE_FORMAT}
              shouldCloseOnSelect
              showTimeSelect
            />
          </div>
        )}
        {accessorial.end_date_required && (
          <div className="px-2">
            <label htmlFor="endDate" className="block mb-2 form-label">
              {t('END_DATE')}
            </label>
            <DatePicker
              id="startDate"
              selected={getMomentDate(end_date)}
              onChange={this.handleValueChange('end_date')}
              customInput={<DateIconButton />}
              dateFormat={DATE_FORMAT}
              shouldCloseOnSelect
              showTimeSelect
            />
          </div>
        )}
        {accessorial.location_required && (
          <div className="px-2">
            <CargomaticSelect
              selected={location}
              fullWidth={false}
              items={locationTypes}
              error={errors.location}
              defaultSelectLabel=""
              label={t('LOCATION')}
              info={t('LOCATION_CUSTOMER_OR_TERMINAL')}
              onSelect={this.handleLocationChange}
              textKey="localizationSlug"
              valueKey="value"
              showCaption
            />
          </div>
        )}
      </div>
    );
  }

  renderDescription() {
    const { t } = this.props;
    const {
      accessorial,
      accessorialRequest: { description },
    } = this.state;

    if (!accessorial) {
      return null;
    }

    if (!accessorial.description_required) {
      return null;
    }

    return (
      <div className="mb-4">
        <CargomaticInput
          value={description}
          type="text"
          label={t('DESCRIPTION', { count: 1 })}
          placeholder={t('ENTER_ACCESSORIAL_DESCRIPTION')}
          onChange={this.handleValueChange('description')}
        />
      </div>
    );
  }

  handleCustomerApprovalChanges = (required, provided) => {
    this.setState({
      customerApprovalRequired: required,
      customerApprovalProvided: provided,
    });
  };

  render() {
    const { t } = this.props;
    const {
      accessorialRequest: { units, rate, name, admin_note },
      errors,
      loading,
    } = this.state;
    const accessorialSelected = !isEmpty(this.state.accessorial);
    const loadingText = t('LOADING');
    const buttonClassName = FEATURE_FLAG_ENABLED_BREADCRUMB
      ? `mr-2 button button-${this.actionsDisabled() ? 'gray' : 'green'}`
      : 'mr-2 button button-green';
    return (
      <div className="">
        {!loading && (
          <div>
            {this.renderAccessorialAutoDeclineWarning()}
            {this.renderSuccess()}
            {this.renderErrors()}
            <div className="flex -mx-2">
              <div className="px-2">
                <CargomaticSelect
                  className="mb-4"
                  selected={this.state.selectedAccessorial}
                  fullWidth={false}
                  items={this.state.accessorials}
                  error={errors.name}
                  defaultSelectLabel={t('SELECT_AN_ACCESSORIAL')}
                  label={this.props.t('ACCESSORIAL')}
                  info={t('TYPE_OF_ACCESSORIAL')}
                  textKey="name"
                  valueKey="slug"
                  onSelect={this.handleSelectAccessorial}
                  showCaption
                />
              </div>
              <div className="px-2">
                <CargomaticInput
                  className="mb-4"
                  onChange={this.handleRateChange}
                  fullWidth={false}
                  inputStyles={{ width: '5rem' }}
                  type="number"
                  label={this.props.t('RATE', { count: 1 })}
                  info={t('ACCESSORIALS_TOOLTIP_RATE')}
                  placeholder={t('RATE', { count: 1 })}
                  disabled={this.isCarrierRateDisabled()}
                  value={rate}
                />
              </div>
              <div className="px-2">
                <CargomaticInput
                  className="mb-4"
                  onChange={this.handleUnitsChange}
                  fullWidth={false}
                  inputStyles={{ width: '5rem' }}
                  type="number"
                  label={this.props.t('UNIT', { count: 2 })}
                  info={t('ACCESSORIALS_TOOLTIP_UNITS')}
                  placeholder={t('UNITS_PLACEHOLDER')}
                  min={1}
                  step={this.state.step}
                  value={units}
                />
              </div>
              <div className="px-2">
                <label className="block mb-2 form-label">
                  {this.props.t('TOTAL')}
                </label>
                {this.renderTotal()}
              </div>
            </div>
            {this.state.showNameField && (
              <div className="mb-4">
                <CargomaticInput
                  onChange={this.handleValueChange('name')}
                  value={name}
                  type="text"
                  label={t('NAME')}
                  placeholder={t('ENTER_ACCESSORIAL_NAME')}
                />
              </div>
            )}
            {this.renderRequiredFields()}
            {this.renderDescription()}
            {this.state.accessorial &&
              featureEnabled(CONFIGURED_FEATURES.SSR) &&
              accessorialSelected && (
                <ViewAccessorialRequestDocumentRequirements
                  containerClassName="mb-4 p-2"
                  accessorial={this.state.accessorial}
                  shipper={this.props.job?.shipper?._id}
                  shipment={this.props.job}
                  handleCustomerApprovalChanges={
                    this.handleCustomerApprovalChanges
                  }
                  setIsCarrierMissingRequiredAccDocuments={
                    this.setIsCarrierMissingRequiredAccDocuments
                  }
                  hasGenericSupportingDocOnly={
                    this.state.hasGenericSupportingDocOnly
                  }
                  setHasGenericSupportingDocOnly={
                    this.setHasGenericSupportingDocOnly
                  }
                />
              )}
            <div className="mb-4">
              <CargomaticInput
                onChange={this.handleValueChange('admin_note')}
                value={admin_note}
                type="text"
                label={this.props.t('NOTE', { count: 2 })}
                info={t('EXTRA_INFO_SENT')}
                placeholder={`${this.props.t('ENTER_YOUR_NOTE')}`}
              />
            </div>
            {}
            <div className="flex justify-end">
              <button
                className={buttonClassName}
                type="button"
                disabled={this.actionsDisabled()}
                onClick={this.handleSave}
              >
                {this.state.saving && <Spinner message={t('SAVING_DOTS')} />}
                {!this.state.saving && <span>{this.props.t('SAVE')}</span>}
              </button>
              <button
                className="button button-secondary"
                type="button"
                // disabled={this.actionsDisabled()}
                onClick={this.handleCancel}
              >
                {this.props.t('CANCEL')}
              </button>
            </div>
          </div>
        )}
        {loading && (
          <div className="p-4">
            <Spinner message={loadingText} />
          </div>
        )}
      </div>
    );
  }
}

AccessorialRequestForm.propTypes = {
  job: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    carrier: PropTypes.shape({
      _id: PropTypes.string,
      name: PropTypes.string,
    }),
    shipper: PropTypes.shape({
      _id: PropTypes.string,
      name: PropTypes.string,
    }),
  }).isRequired,
  accessorialRequest: PropTypes.shape({
    _id: PropTypes.string,
    name: PropTypes.string.isRequired,
    rate: PropTypes.number,
    units: PropTypes.number,
    admin_note: PropTypes.string,
  }),
  onSaveSuccess: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  existingRequests: PropTypes.array,
};

export default withTranslation()(AccessorialRequestForm);
